/**
 * `/admin/cache` — stats and purge (SPEC §9.1, §10).
 *
 * A purge is a targeted `SCAN` + `UNLINK` over the report key prefix, not a
 * `FLUSHDB`: the same Redis holds every session, and signing the whole company
 * out to clear one report's cache would be a memorable way to learn that.
 *
 * Purging is rarely necessary — `manifest_hash` is part of every cache key, so
 * editing a report already invalidates its entries. It is here for the case
 * the key cannot see: the *data* changed underneath an unchanged report.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { Button, Card, ConfirmDialog, Grid } from '../../components/admin/ui'
import { ErrorState, Skeleton } from '../../components/States'
import { adminApi } from '../../lib/admin'
import { formatInteger } from '../../lib/format'

export function AdminCache() {
  const queryClient = useQueryClient()
  const [confirmingPurgeAll, setConfirmingPurgeAll] = useState(false)
  const stats = useQuery({ queryKey: ['admin', 'cache'], queryFn: adminApi.cache })

  const purge = useMutation({
    mutationFn: (target: { slug: string } | { all: true }) => adminApi.purgeCache(target),
    onSuccess: () => {
      setConfirmingPurgeAll(false)
      void queryClient.invalidateQueries({ queryKey: ['admin', 'cache'] })
    },
  })

  return (
    <Card
      title="Report cache"
      description="Cached result sets and freshness probes, by report. Sessions are stored separately and are never touched."
      actions={
        <Button
          variant="danger"
          disabled={purge.isPending}
          onClick={() => {
            setConfirmingPurgeAll(true)
          }}
        >
          Purge everything
        </Button>
      }
    >
      {purge.error !== null && (
        <div className="mb-3">
          <ErrorState error={purge.error} />
        </div>
      )}

      {stats.isPending ? (
        <Skeleton rows={4} />
      ) : stats.error !== null ? (
        <ErrorState
          error={stats.error}
          onRetry={() => {
            void stats.refetch()
          }}
        />
      ) : (
        <>
          {stats.data.unparsed_keys > 0 && (
            <p
              role="alert"
              className="mb-3 rounded-[var(--radius-md)] border border-[var(--color-negative)]/40 bg-[var(--color-negative)]/5 px-3 py-2 text-[length:var(--text-xs)]"
            >
              <span className="numeric">{stats.data.unparsed_keys}</span> cached keys do not match
              the expected format. The key layout changed and{' '}
              <span className="numeric">portal/reporting/cache.py</span> was not updated with it.
            </p>
          )}

          {stats.data.reports.length === 0 ? (
            <p className="text-[length:var(--text-sm)] text-[var(--color-ink-muted)]">
              Nothing cached.
            </p>
          ) : (
            <Grid head={['Report', 'Result sets', 'Freshness probes', '']}>
              {stats.data.reports.map((row) => (
                <tr key={row.slug} className="border-b border-[var(--color-rule)] last:border-0">
                  <td className="px-2 py-1.5">
                    <div className="font-medium">{row.title ?? 'Not in the registry'}</div>
                    <div className="numeric text-[length:var(--text-2xs)] text-[var(--color-ink-muted)]">
                      {row.slug}
                    </div>
                  </td>
                  <td className="numeric px-2 py-1.5 text-right">
                    {formatInteger(row.result_keys)}
                  </td>
                  <td className="numeric px-2 py-1.5 text-right">
                    {formatInteger(row.freshness_keys)}
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <Button
                      disabled={purge.isPending}
                      onClick={() => {
                        purge.mutate({ slug: row.slug })
                      }}
                    >
                      Purge
                    </Button>
                  </td>
                </tr>
              ))}
            </Grid>
          )}

          <p className="numeric mt-3 text-[length:var(--text-xs)] text-[var(--color-ink-muted)]">
            {formatInteger(stats.data.total_keys)} keys total
          </p>
        </>
      )}

      <ConfirmDialog
        open={confirmingPurgeAll}
        title="Purge the entire report cache?"
        confirmLabel="Purge everything"
        body="Every report recomputes on its next run. Nobody is signed out and no data is lost — the next few report views are just slower."
        onCancel={() => {
          setConfirmingPurgeAll(false)
        }}
        onConfirm={() => {
          purge.mutate({ all: true })
        }}
      />
    </Card>
  )
}
